import React, {useState, useEffect, useRef} from 'react'
import styles from './Scheduler.css'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import SelectedLayer, {enableScroll} from './SelectedLayer'
import {
  getSearchTimes,
  getDayRange,
  getMonthRecurrenceDates
} from '../../../utils/recurrence'
import {_L, useLocaleState} from '../../../context/LocaleContext'

import {formatDate, getDate} from '../../../utils/time'
import SchedulerContent from './SchedulerContent'
import SchedulerContext from './SchedulerContext'

export const LAYOUT_TYPE_HORIZONTAL = 'horizontal'
export const LAYOUT_TYPE_VERTICAL = 'vertical'

const TIME_FORMAT = 'Y-m-d'
const TIME_CELL = 0.5

// Rounds minutes to the half hour ( 0 or .5)
const roundMinutes = minutes => Math.round(minutes / 60) / 2

const Scheduler = props => {
  const {
    searchFilter,
    meeting,
    recurrence,
    scheduleDates,
    workDates,
    layout,
    onChange,
    onUpdateMeeting
  } = props

  let _selectedLayer = useRef(null)
  let _container = useRef(null)
  let _content = useRef(null)

  // These where instance values
  let _selectedTimes = useRef({})
  let _conflict = useRef([])

  const [times, setTimes] = useState({})
  const [isSelect, setIsSelect] = useState(false)
  const [width, setWidth] = useState(900)
  const [displayConflict, setDisplayConflict] = useState(-1)
  const localeState = useLocaleState()

  useEffect(() => {
    const width = window.innerWidth
    let searchTimes = getSearchTimes(recurrence, meeting, searchFilter)

    let meetingTimes =
      meeting.selectedTimes.length > 0
        ? meeting.selectedTimes
        : searchTimes.searchTimes

    const selectedTimes = _configureSelectedTimes(meetingTimes)

    const times = configureTimes(props, meetingTimes)

    setTimes(times)

    onUpdateMeeting({times})

    setIsSelect(Object.keys(selectedTimes).length > 0)
    setWidth(width)
  }, [recurrence])

  const onSelectStart = (time, rowDate, htmlCell) => {
    const rowData = times[rowDate.format(TIME_FORMAT)]

    if (
      rowData.selected.start !== -1 &&
      ((rowData.conflicted && rowData.userResolveConflict) ||
        !rowData.conflicted)
    ) {
      const {start, end} = rowData.selected
      if (start <= time && time <= end) {
        _selectedLayer.current.multiSelectStart({
          rowConfig: rowData,
          row: rowDate,
          cell: time,
          htmlCell: {
            offsetTop: htmlCell.target.offsetTop,
            offsetLeft: htmlCell.target.offsetLeft
          },
          selectedTimes: {
            start: start,
            end: end
          }
        })
        return
      }
    }

    if (
      (rowData.conflicted && !rowData.userResolveConflict) ||
      (rowData.userResolveConflict && rowData.selected.start === -1)
    ) {
      _selectedLayer.current.multiSelectStart({
        rowConfig: rowData,
        row: rowDate,
        cell: time,
        htmlCell: {
          offsetTop: htmlCell.target.offsetTop,
          offsetLeft: htmlCell.target.offsetLeft
        },
        selectedTimes: {
          start: time,
          end: time
        }
      })
      rowData.userResolveConflict = true
      rowData.busy = rowData.busy.map(item => {
        return {
          ...item,
          conflict: false
        }
      })
      rowData.selected = {
        start: time,
        end: time
      }

      saveTimes(rowData, {key: rowDate.format(TIME_FORMAT)})
      const idx = _conflict.current.findIndex(item => {
        return item.day.format(TIME_FORMAT) === rowDate.format(TIME_FORMAT)
      })
      _conflict.current.splice(idx, 1)
      return
    }

    if (isSelect) return

    let selectedTimes = {
      start: time,
      end: time
    }

    saveTimes(selectedTimes)
    _selectedLayer.current.firstSelect = true
    _selectedLayer.current.multiSelectStart({
      rowConfig: rowData,
      row: rowDate,
      cell: time,
      htmlCell: {
        offsetTop: htmlCell.target.offsetTop,
        offsetLeft: htmlCell.target.offsetLeft
      },
      selectedTimes: selectedTimes
    })
  }

  const onRemoveSelected = rowDate => {
    const dateFormat = rowDate.format(TIME_FORMAT)
    let selectedRow = times[dateFormat]
    if (selectedRow.conflicted || selectedRow.userResolveConflict) {
      selectedRow.selected = {
        start: -1,
        end: -1
      }
      saveTimes(selectedRow, {key: dateFormat})
    } else {
      _deselectedTimes()
      _conflict.current = []
    }
  }

  const updateSelected = (timeRange, rowDate) => {
    const dateFormat = rowDate.format(TIME_FORMAT)
    let selectedRow = times[dateFormat]

    if (selectedRow.conflicted || selectedRow.userResolveConflict) {
      selectedRow.selected = timeRange
      saveTimes(selectedRow, {key: dateFormat})
    } else {
      saveTimes(timeRange)
    }
  }

  const onSelectMove = e => {
    const {offsetLeft, offsetTop} = _container.current
    const {scrollLeft, scrollTop} = _content.current
    let x = e.pageX - offsetLeft + scrollLeft
    let y = e.pageY - offsetTop + scrollTop

    if (e.targetTouches) {
      x = e.targetTouches[0].pageX - offsetLeft + scrollLeft
      y = e.targetTouches[0].pageY - offsetTop + scrollTop
    }

    _selectedLayer.current.resizeMove(x, y)
  }

  const onSelectUp = () => {
    enableScroll()
    _selectedLayer.current.onMouseUpRight()
    _selectedLayer.current.onMouseUpLeft()
    _selectedLayer.current.firstSelect = false
  }

  const onSelectEnd = () => onSelectUp()

  const isException = date => {
    return !(
      date.selected.start === _selectedTimes.current.start &&
      date.selected.end === _selectedTimes.current.end
    )
  }

  const saveTimes = (selectedTimes, options) => {
    let newTimes = {...times}

    if (options) {
      newTimes[options.key] = selectedTimes
    } else {
      newTimes = configureTimes(props)
      for (let key in times) {
        newTimes[key].selected = selectedTimes
      }
      _selectedTimes.current = selectedTimes
    }

    setTimes(newTimes)

    const seriesEnd = _selectedTimes.current.end + 0.5

    let series = {
      start:
        (_selectedTimes.current.start ^ 0) === _selectedTimes.current.start
          ? _selectedTimes.current.start + ':00'
          : _selectedTimes.current.start - 0.5 + ':30',
      end:
        (seriesEnd ^ 0) === seriesEnd
          ? seriesEnd + ':00'
          : seriesEnd - 0.5 + ':30',
      dates: []
    }

    let exceptions = []

    Object.values(newTimes).map(date => {
      if (date.selected.start !== -1) {
        series.dates.push(date.date)
        if (isException(date)) {
          const exceptionsEnd = date.selected.end + 0.5
          exceptions.push({
            start:
              (date.selected.start ^ 0) === date.selected.start
                ? date.selected.start + ':00'
                : date.selected.start - 0.5 + ':30',
            end:
              (exceptionsEnd ^ 0) === exceptionsEnd
                ? exceptionsEnd + ':00'
                : exceptionsEnd - 0.5 + ':30',
            date: date.date,
            // the exception is for the last date pushed to series.dates
            index: series.dates.length - 1
          })
        }
      }
    })

    onChange({
      series,
      exceptions
    })
  }

  const onSolveConflict = (date, conflictTimes) => {
    saveTimes(conflictTimes, {key: date.format(TIME_FORMAT)})
  }

  const _saveConflictPosition = pos => {
    const idx = _conflict.current.findIndex(item => {
      return pos.day.format(TIME_FORMAT) === item.day.format(TIME_FORMAT)
    })
    if (idx === -1) _conflict.current.push(pos)
  }

  const _cleanConflict = () => {
    setDisplayConflict(-1)
  }

  useEffect(
    () => {
      _selectedLayer.current.hide()

      // Calculate the times based on the search fields.
      let searchTimes = getSearchTimes(recurrence, meeting, searchFilter)

      let meetingTimes =
        meeting.selectedTimes.length > 0
          ? meeting.selectedTimes
          : searchTimes.searchTimes
      let times = configureTimes(props, meetingTimes)

      setTimes(times)
      setIsSelect(false)
    },
    [recurrence, scheduleDates, meeting.scheduleDates]
  )

  const mergeSelectedTimes = (dates, selectedTimes) => {
    if (selectedTimes.length) {
      return dates.map((date, idx) => {
        date.selectedTime = selectedTimes[idx]
        return date
      })
    } else {
      return dates
    }
  }

  const configureTimes = (props, preSelectedTimes = []) => {
    const {
      date_start,
      date_end,
      weekDays,
      frequencyDay,
      frequency,
      recurrenceType
    } = recurrence

    let {selectedTimes} = meeting

    // TODO: Check if selectedTimes is valid instead?
    if (preSelectedTimes.length > 0) {
      selectedTimes = preSelectedTimes
    }

    let times = {}

    let monthDays = []
    let weeklyDays = []
    let dailyDays = []

    // Use only the date when generating the times
    let start = getDate(date_start)
    let end = date_end || date_start

    end = getDate(end)

    // No recurrence
    if (recurrenceType === 'none') {
      let selected = null
      if (selectedTimes.length) {
        selected = selectedTimes[0]
      }
      times[start.format(TIME_FORMAT)] = _configureRow(start, selected)
      return times
    }

    // Monthly calculation
    if (recurrenceType === 'month') {
      let selected = null
      if (selectedTimes.length) {
        selected = selectedTimes[0]
      }
      monthDays = getDayRange(
        recurrenceType,
        start,
        end,
        weekDays,
        frequencyDay,
        frequency
      )
      let dateAndTimes = mergeSelectedTimes(monthDays, selectedTimes)
      times = dateAndTimes.reduce((acc, curVal) => {
        acc[curVal.format(TIME_FORMAT)] = _configureRow(curVal, selected)
        return acc
      }, {})

      return times
    }

    // Daily calculation
    if (recurrenceType === 'day') {
      let selected = null
      if (selectedTimes.length) {
        selected = selectedTimes[0]
      }
      dailyDays = getDayRange(recurrenceType, start, end)
      let dateAndTimes = mergeSelectedTimes(dailyDays, selectedTimes)
      times = dateAndTimes.reduce((acc, curVal) => {
        acc[curVal.format(TIME_FORMAT)] = _configureRow(curVal, selected)
        return acc
      }, {})

      return times
    }

    // Weekly calculation
    if (recurrenceType === 'week') {
      let selected = null
      if (selectedTimes.length) {
        selected = selectedTimes[0]
      }
      weeklyDays = getDayRange(recurrenceType, start, end, weekDays)
      let dateAndTimes = mergeSelectedTimes(weeklyDays, selectedTimes)
      times = dateAndTimes.reduce((acc, curVal) => {
        acc[curVal.format(TIME_FORMAT)] = _configureRow(curVal, selected)
        return acc
      }, {})

      return times
    }
  }

  const _configureSelectedTimes = selectedTimes => {
    if (selectedTimes.length === 0) {
      return {}
    }

    let result = {}
    selectedTimes.map(time => {
      const format =
        parseInt(time.start.format('G')) +
        (time.start.format('i') === '30' ? 0.5 : 0) +
        '-' +
        (parseInt(time.end.format('G')) +
          (time.end.format('i') === '30' ? 0.5 : 0))

      if (result.hasOwnProperty(format)) {
        result[format].count++
        result[format].arr.push(time)
      } else {
        result[format] = {
          count: 1,
          arr: [time]
        }
      }
    })

    let max = -1
    let times = {}
    for (let key in result) {
      const obj = result[key]
      const time = {
        start: parseFloat(key.split('-')[0]),
        end: parseFloat(key.split('-')[1]) - 0.5
      }
      obj.arr.map(item => {
        times[item.start.format(TIME_FORMAT)] = time
      })
      if (obj.count > max) {
        max = obj.count
        _selectedTimes.current = time
      }
    }

    return times
  }

  const _deselectedTimes = () => {
    let newTimes = {...times}
    for (let key in newTimes) {
      newTimes[key].selected = {
        start: -1,
        end: -1
      }
    }
    _selectedTimes.current = {
      start: -1,
      end: -1
    }
    setTimes(newTimes)
    setIsSelect(false)

    onChange({
      series: {start: '', end: '', dates: []},
      exceptions: []
    })
  }

  const _configureRow = (time, selectedTime) => {
    const timeFormat = time.format(TIME_FORMAT)
    const busyDates = _getBusyDatesForTime(timeFormat)

    let busy = []
    if (busyDates.length > 0) {
      for (let i = 0; i < busyDates.length; i++) {
        busy.push(_getBusyData(busyDates[i]))
      }
    }

    let start = -1
    let end = -1

    if (selectedTime) {
      const {start: startDate, end: endDate} = selectedTime
      start = startDate.getHours() + roundMinutes(startDate.getMinutes())

      // If the end date ends on the hour then subtract 30 minutes
      let endMinutes = endDate.getMinutes()
      end = endMinutes === 0 ? endDate.getHours() - 0.5 : endDate.getHours()
    }

    return {
      date: time,
      userResolveConflict: false,
      conflicted: false,
      selected: {
        start,
        end
      },
      busy: busy
    }
  }

  const _getBusyDatesForTime = timeFormat => {
    let busyDates = []
    for (let i = 0; i < scheduleDates.length; i++) {
      const schedule = scheduleDates[i]
      if (timeFormat === schedule.start.format(TIME_FORMAT)) {
        busyDates.push(schedule)
      }
    }

    return busyDates
  }

  const _getBusyData = busy => {
    const {start, end} = busy
    const minutesStart = parseInt(start.format('i'))
    const minutesEnd = parseInt(end.format('i'))
    const timeStart =
      parseInt(start.format('G')) +
      (minutesStart > 30 ? 1 : minutesStart === 0 ? 0 : TIME_CELL)
    const timeEnd =
      parseInt(end.format('G')) +
      (minutesEnd > 30 ? 1 : minutesEnd === 0 ? 0 : TIME_CELL)
    let blocks = (timeEnd - timeStart) * 2
    if (timeStart > timeEnd) {
      blocks = (timeStart - timeEnd) * 2
    }
    return {
      conflict: false,
      start: timeStart < timeEnd ? timeStart : timeEnd,
      end: timeStart > timeEnd ? timeStart : timeEnd,
      blocks: blocks
    }
  }

  const getContainerHeight = width => {
    const {recurrenceType} = recurrence

    let offset = width < 440 ? 120 : width < 1024 ? 480 : 360

    // TODO: It would be better if we can manage the height using CSS only.
    return recurrenceType === 'none' || recurrenceType === 'day'
      ? `calc(100vh - ${offset + 30}px)`
      : `calc(100vh - ${offset + 130}px)`
  }

  const _getContentHeight = () => {
    return _conflict.current.length !== 0
      ? 'calc(100% - 130px)'
      : 'calc(100% - 30px)'
  }

  const _getWidthScheduler = () => {
    let width = 153
    const {start, end} = workDates
    for (let i = start; i <= end; i = i + 0.5) {
      width += 60
    }
    width += 60
    return width
  }

  let _displayConflict = layout === LAYOUT_TYPE_VERTICAL ? -1 : 0

  const onDisplayConflict = () => {
    if (layout === LAYOUT_TYPE_HORIZONTAL) {
      const pos = _conflict.current[_displayConflict]
      _content.current.scrollLeft = pos.x + pos.width - 200
      _content.current.scrollTop = pos.y - 200

      const rowData = times[pos.day.format(TIME_FORMAT)]
      if (rowData.selected.start === -1) {
        _displayConflict++
        if (_displayConflict >= _conflict.current.length) {
          _displayConflict = 0
        }
        return
      }

      _selectedLayer.current.multiSelectStart({
        rowConfig: rowData,
        row: pos.day,
        cell: rowData.selected.start,
        htmlCell: {
          offsetTop: pos.y,
          offsetLeft: pos.x + pos.width
        },
        selectedTimes: rowData.selected
      })
      rowData.userResolveConflict = true
      rowData.busy = rowData.busy.map(item => {
        return {
          ...item,
          conflict: false
        }
      })
      const idx = _conflict.current.findIndex(item => {
        return item.day.format(TIME_FORMAT) === pos.day.format(TIME_FORMAT)
      })
      _conflict.current.splice(idx, 1)
      _displayConflict++
      saveTimes(rowData, {key: pos.day.format(TIME_FORMAT)})
      if (_displayConflict >= _conflict.current.length) {
        _displayConflict = 0
      }
    }

    if (layout === LAYOUT_TYPE_VERTICAL) {
      const pos = _conflict.current[_displayConflict]
      setDisplayConflict(pos)
    }
  }

  const onLeftArrowConflict = () => {
    if (_displayConflict >= _conflict.current.length) {
      _displayConflict = _conflict.current.length - 1
    }
    _displayConflict--
    if (_displayConflict < 0) {
      _displayConflict = _conflict.current.length - 1
    }
    onDisplayConflict()
    _selectedLayer.current.hide()
    _findVerticalPosForConflict()
  }

  const onRightArrowConflict = () => {
    _displayConflict++
    if (_displayConflict >= _conflict.current.length) {
      _displayConflict = 0
    }
    onDisplayConflict()
    _selectedLayer.current.hide()
    _findVerticalPosForConflict()
  }

  const getDayOfWeekIndex = ({weekDays}) => {
    let index = weekDays.findIndex(item => item === 1)
    return index + 1
  }

  const _findVerticalPosForConflict = () => {
    const conflictDate = _conflict.current[_displayConflict]

    const {
      date_start: startDate,
      date_end: endDate,
      weekDays,
      recurrenceType,
      frequency,
      frequencyDay
    } = recurrence

    let time = startDate
    let end = endDate || startDate
    let count = 0
    while (time.getTime() <= end.getTime()) {
      // weekly
      if (recurrenceType === 'week') {
        if (time.format('w') !== getDayOfWeekIndex({weekDays})) {
          time = time.add(Date.DAY, 1)
          continue
        }
      }

      // monthly
      if (recurrenceType === 'month') {
        let [firstDate] = getMonthRecurrenceDates(
          new Date(
            conflictDate.day.getFullYear(),
            conflictDate.day.getMonth(),
            conflictDate.day.getDate() - 1
          ),
          endDate,
          frequencyDay,
          frequency
        )
        time = firstDate
      }

      count++

      if (time.format(TIME_FORMAT) === conflictDate.day.format(TIME_FORMAT)) {
        break
      }

      if (recurrenceType !== 'month') {
        time = time.add(Date.DAY, 1)
      } else {
        time = time.add(Date.MONTH, 1)
      }
    }

    _content.current.scrollTop = (count - 1) * 60
  }

  let context = {
    layout,
    workDates,
    scheduleDates,
    onSelectStart: onSelectStart,
    onSelectEnd: onSelectEnd,
    hideSelectedLayer: _selectedLayer.current
      ? _selectedLayer.current.hide
      : () => {},
    onSolveConflict: onSolveConflict,
    saveConflictPos: _saveConflictPosition,
    cleanDisplayConflict: _cleanConflict
  }

  const borderDates = (
    formatDate(recurrence.date_start, 'D MMMMM') +
    ' - ' +
    formatDate(recurrence.date_end, 'D MMMM')
  ).toUpperCase()

  const horizontalConflictView = (
    <div className={styles.conflictView}>
      <BorderDateText>{borderDates}</BorderDateText>
      <ConflictButton onClick={onDisplayConflict}>
        {_L({key: 'Next Conflict', localeState})}
      </ConflictButton>
    </div>
  )

  const verticalConflictView = (
    <div className={styles.conflictView}>
      <div className={styles.containerArrow} onClick={onLeftArrowConflict}>
        <i className={styles.leftArrow} />
      </div>
      <div style={{width: '90%'}}>
        <BorderDateText>{borderDates}</BorderDateText>
        <ConflictCountText>
          {_conflict.current.length +
            ' ' +
            _L({key: 'CONFLICT DATE', localeState})}
        </ConflictCountText>
      </div>
      <div className={styles.containerArrow} onClick={onRightArrowConflict}>
        <i className={styles.rightArrow} />
      </div>
    </div>
  )

  return (
    <div
      className={styles.container}
      style={{height: getContainerHeight(width)}}
      ref={_container}
    >
      <FieldTitleText>
        {_L({key: 'Choose Meeting Time', localeState})}
      </FieldTitleText>
      <div
        className={styles.content}
        style={{height: _getContentHeight()}}
        ref={_content}
        onTouchMove={onSelectMove}
        onMouseMove={onSelectMove}
        onMouseUp={onSelectUp}
        onTouchEnd={onSelectUp}
      >
        <SchedulerContext.Provider value={context}>
          <SelectedLayer
            ref={_selectedLayer}
            onRemoveSelected={onRemoveSelected}
            updateSelected={updateSelected}
            widthScheduler={_getWidthScheduler()}
          />
          <SchedulerContent
            viewDates={times}
            displayConflict={displayConflict}
          />
        </SchedulerContext.Provider>
      </div>
      {_conflict.current.length !== 0 &&
        (layout === LAYOUT_TYPE_HORIZONTAL
          ? horizontalConflictView
          : verticalConflictView)}
    </div>
  )
}

Scheduler.defaultProps = {
  layout: LAYOUT_TYPE_VERTICAL,
  recurrence: {},
  scheduleDates: [],
  selectedTimes: []
}

Scheduler.propTypes = {
  layout: PropTypes.string.isRequired,
  recurrence: PropTypes.object.isRequired,
  scheduleDates: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired
}

export default Scheduler

const BorderDateText = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #000000;
  width: 90%;
`
const ConflictCountText = styled.div`
  font-size: 10px;
  color: #ef5350;
`

const FieldTitleText = styled.div`
  font-size: 16px;
  color: #a4a9b7;
  font-weight: 600;
`

const ConflictButton = styled.button`
  font-size: 14px;
  font-weight: normal;
  box-sizing: border-box;
  background-color: #f3f5fb;
  border: 2px solid #f3f5fb;
  color: #a4a9b7;
  border-radius: 6px;
  outline: none;
  width: 100px;
  height: 40px;
`
